import { Stack } from '@mantine/core';
import type { TFunction } from 'i18next';
import { downloadCSV } from '../../../../helpers/csv-download';
import { formatDate } from '../../../../helpers/date-time-helpers';
import { primaryBase, skyLight } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import { TableColumn } from '../../../data-management/components/table';
import { RiskBadge } from '../risk-analysis';
import type { RiskAnalysisInitiative } from '../risk-analysis.client.type';
import { getRiskCategory } from '../risk-analysis.helpers';
import { TruncatedText } from '../truncated-text';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
function CompletionBar({ percentage }: { percentage: number }) {
  return (
    <Stack gap={1}>
      <Text size="small" weight="medium">
        {percentage}%
      </Text>
      <div
        style={{
          width: '100%',
          height: '4px',
          backgroundColor: skyLight,
          borderRadius: '2px',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: primaryBase,
            borderRadius: '2px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </Stack>
  );
}

const getInitiativeTableColumns = (t: TFunction): TableColumn<RiskAnalysisInitiative>[] => {
  return [
    {
      key: 'name',
      label: `${t('targets.initiative_name')}`,
      width: '20%',
      sortable: true,
      render: (_, initiative) => <Text weight="medium">{initiative.name}</Text>,
    },
    {
      key: 'description',
      label: t('common.description'),
      width: '20%',
      render: (_, initiative) => <TruncatedText text={initiative.description} onToggle={() => {}} />,
    },
    {
      key: 'completion_percentage',
      label: `${t('strategy.initiatives.table.completion')}`,
      width: '12%',
      sortable: true,
      render: (_, initiative) => <CompletionBar percentage={initiative.completion_percentage} />,
    },
    {
      key: 'start_date',
      label: `${t('strategy.initiatives.form.start_date_label')}`,
      width: '10%',
      sortable: true,
      render: (_, initiative) =>
        initiative.start_date ? formatDate(initiative.start_date, { timezone, format: 'MM/DD/YYYY' }) : 'No date',
    },
    {
      key: 'end_date',
      label: `${t('strategy.initiatives.form.end_date_label')}`,
      width: '10%',
      sortable: true,
      render: (_, initiative) =>
        initiative.end_date ? formatDate(initiative.end_date, { timezone, format: 'MM/DD/YYYY' }) : 'No date',
    },
    {
      key: 'flora_annotations.risk_category' as keyof RiskAnalysisInitiative,
      label: `${t('targets.risk_category')}`,
      width: '13%',
      sortable: true,
      render: (_, initiative) => {
        const riskCategory = getRiskCategory(initiative.flora_annotations?.risk_score);
        return <RiskBadge color={riskCategory.color}>{riskCategory.label}</RiskBadge>;
      },
    },
    {
      key: 'flora_annotations.risk_score' as keyof RiskAnalysisInitiative,
      label: `${t('targets.risk_score')}`,
      width: '10%',
      sortable: true,
      render: (_, initiative) => initiative.flora_annotations?.risk_score?.toFixed(0) ?? 'N/A',
    },
  ];
};

const handleDownloadInitiativeTableCSV = (initiatives: RiskAnalysisInitiative[], t: TFunction) => {
  try {
    const headers = [
      `${t('targets.initiative_name')}`,
      `${t('common.description')}`,
      `${t('strategy.initiatives.table.completion')} %`,
      `${t('strategy.initiatives.form.start_date_label')}`,
      `${t('strategy.initiatives.form.end_date_label')}`,
      `${t('targets.risk_category')}`,
      `${t('targets.risk_score')}`,
    ];

    const rows = initiatives.map((initiative) => [
      initiative.name,
      initiative.description,
      initiative.completion_percentage,
      initiative.start_date ? formatDate(initiative.start_date, { timezone, format: 'MM/DD/YYYY' }) : 'No date',
      initiative.end_date ? formatDate(initiative.end_date, { timezone, format: 'MM/DD/YYYY' }) : 'No date',
      getRiskCategory(initiative.flora_annotations?.risk_score).label,
      initiative.flora_annotations?.risk_score?.toFixed(0) ?? 'N/A',
    ]);

    downloadCSV([headers, ...rows], 'initiatives.csv');
  } catch (error) {
    console.error('Error downloading CSV:', error);
  }
};

export { CompletionBar, getInitiativeTableColumns, handleDownloadInitiativeTableCSV };
